/**
 * Style + arbitrary data + content accessors — port 1:1 din legacy
 * common.js lines 591-718.
 *   - Node/NodeList.prototype.css
 *   - Node.prototype.offset
 *   - Node.prototype.data / removeData
 *   - Node.prototype.html / text / val
 *   - Node.prototype.getProp / dxGetProp
 *
 * Foloseste Symbol-uri pentru a evita coliziuni cu alte libs.
 */

import { isset } from './util.js';

export const DATA_KEY = Symbol('NavData');

interface NodeWithData extends Node {
    [DATA_KEY]?: Record<string, unknown>;
}

Node.prototype.css = function (this: Node, prop: string | Record<string, string>, val?: string) {
    const style = (this as HTMLElement).style;
    if (isset(val)) {
        if (!val) {
            style.removeProperty(prop as string);
        } else {
            (style as unknown as Record<string, string>)[prop as string] = val;
        }
    } else {
        Object.assign(style, prop);
    }
    return this;
} as Node['css'];

NodeList.prototype.css = function (this: NodeList, prop: string | Record<string, string>, val?: string) {
    this.forEach((node) => {
        (node as Node).css(prop as string, val as string);
    });
    return this;
} as NodeList['css'];

Node.prototype.offset = function (this: Node) {
    const el = this as Element;
    if (!el.getClientRects?.().length) {
        return { top: 0, left: 0 };
    }
    const rect = el.getBoundingClientRect();
    const view = el.ownerDocument.defaultView!;
    return {
        top:  rect.top  + view.pageYOffset,
        left: rect.left + view.pageXOffset,
    };
};

Node.prototype.data = function (this: Node, key: string, val?: unknown) {
    const node = this as NodeWithData;
    if (!node[DATA_KEY]) node[DATA_KEY] = {};
    if (!isset(val)) {
        return node[DATA_KEY]![key] ?? null;
    }
    node[DATA_KEY]![key] = val;
    return node;
} as Node['data'];

Node.prototype.removeData = function (this: Node, key: string) {
    const node = this as NodeWithData;
    if (!node[DATA_KEY]) node[DATA_KEY] = {};
    delete node[DATA_KEY]![key];
    return this;
};

Node.prototype.html = function (this: Node, content?: string) {
    if (isset(content)) {
        (this as Element).innerHTML = content!;
        return this;
    }
    return (this as Element).innerHTML;
} as Node['html'];

Node.prototype.text = function (this: Node, content?: string) {
    if (isset(content)) {
        this.textContent = content!;
        return this;
    }
    return this.textContent ?? '';
} as Node['text'];

Node.prototype.val = function (this: Node, content?: string) {
    const input = this as HTMLInputElement;
    if (isset(content)) {
        input.value = content!;
        return this;
    }
    return (input.value ?? '').trim();
} as Node['val'];

Node.prototype.getProp = function (this: Node, name: string) {
    return window.getComputedStyle(this as Element).getPropertyValue(name);
};

Node.prototype.dxGetProp = function (this: Node, name: string) {
    return window.getComputedStyle(this as Element).getPropertyValue(name);
};
